import React from "react";
import "@/App.css";
import "@/lib/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SeasonProvider, useSeason } from "@/contexts/SeasonContext";
import { Toaster } from "@/components/ui/sonner";
import DashboardLayout from "@/components/Layout/DashboardLayout";

import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import ManagerDashboard from "@/pages/ManagerDashboard";
import SponsorDashboard from "@/pages/SponsorDashboard";
import SponsorsPage from "@/pages/SponsorsPage";
import SponsorDetailPage from "@/pages/SponsorDetailPage";
import RightsPage from "@/pages/RightsPage";
import MatchesPage from "@/pages/MatchesPage";
import RequestsPage from "@/pages/RequestsPage";
import CreateRequestPage from "@/pages/CreateRequestPage";
import RequestDetailPage from "@/pages/RequestDetailPage";
import ScheduleImportPage from "@/pages/ScheduleImportPage";
import AuditLogPage from "@/pages/AuditLogPage";
import ReportsPage from "@/pages/ReportsPage";
import UsersPage from "@/pages/UsersPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import ClubsPage from "@/pages/ClubsPage";
import StadiumsPage from "@/pages/StadiumsPage";
import AuthCallback from "@/pages/AuthCallback";

function DashboardRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <AdminDashboard />;
  if (user.role === "account_manager") return <ManagerDashboard />;
  if (user.role === "spo_viewer") return <SponsorDashboard />;
  if (user.role === "sponsor") return <SponsorDashboard />;
  // Custom roles: route based on permissions
  const perms = user.permissions || [];
  if (perms.includes("manage_settings")) return <AdminDashboard />;
  if (perms.includes("review_requests") || perms.includes("create_requests_on_behalf")) return <ManagerDashboard />;
  return <ManagerDashboard />;
}

function ProtectedRoute({ children, roles, permissions, anyPermission }) {
  const { user, isAuthenticated } = useAuth();
  const { seasonOpen } = useSeason();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (permissions && !permissions.every(p => user?.permissions?.includes(p))) return <Navigate to="/dashboard" replace />;
  if (anyPermission && !anyPermission.some(p => user?.permissions?.includes(p))) return <Navigate to="/dashboard" replace />;
  return <DashboardLayout>{children}</DashboardLayout>;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
    <SeasonProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
          <Route path="/sponsors" element={<ProtectedRoute permissions={["view_sponsors"]}><SponsorsPage /></ProtectedRoute>} />
          <Route path="/sponsors/:id" element={<ProtectedRoute permissions={["view_sponsors"]}><SponsorDetailPage /></ProtectedRoute>} />
          <Route path="/rights" element={<ProtectedRoute permissions={["view_rights"]}><RightsPage /></ProtectedRoute>} />
          <Route path="/clubs" element={<ProtectedRoute anyPermission={["view_clubs", "create_clubs", "edit_clubs", "delete_clubs"]}><ClubsPage /></ProtectedRoute>} />
          <Route path="/stadiums" element={<ProtectedRoute anyPermission={["view_stadiums", "create_stadiums", "edit_stadiums", "delete_stadiums"]}><StadiumsPage /></ProtectedRoute>} />
          <Route path="/roles" element={<Navigate to="/settings" replace />} />
          <Route path="/matches" element={<ProtectedRoute anyPermission={["view_matches", "create_matches", "edit_matches", "delete_matches"]}><MatchesPage /></ProtectedRoute>} />
          <Route path="/matches/import" element={<ProtectedRoute permissions={["import_matches"]}><ScheduleImportPage /></ProtectedRoute>} />

          <Route path="/requests" element={<ProtectedRoute anyPermission={["view_requests", "create_requests", "create_requests_on_behalf", "review_requests"]}><RequestsPage /></ProtectedRoute>} />
          <Route path="/requests/new" element={<ProtectedRoute anyPermission={["create_requests", "create_requests_on_behalf"]}><CreateRequestPage /></ProtectedRoute>} />
          <Route path="/requests/:id" element={<ProtectedRoute anyPermission={["view_requests", "create_requests", "create_requests_on_behalf", "review_requests"]}><RequestDetailPage /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute anyPermission={["view_reports", "view_usage_report", "create_reports"]}><ReportsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute anyPermission={["create_users", "edit_users", "delete_users"]}><UsersPage /></ProtectedRoute>} />
          <Route path="/audit-log" element={<ProtectedRoute permissions={["view_audit_log"]}><AuditLogPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute anyPermission={["manage_settings", "create_users", "edit_users", "delete_users", "manage_roles", "view_audit_log"]}><AdminSettingsPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </SeasonProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
